import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Sparkles, Loader2, Send, Trash2, Plus, Eye, BarChart3, Save, TestTube2, ArrowUp, ArrowDown } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, PanelLoader, EmptyState, Card, Field, Chip, inputCls } from './shared';
import { toast } from 'sonner';

const BLOCK_TYPES = [['heading', 'Heading'], ['text', 'Paragraph'], ['button', 'Button'], ['image', 'Image'], ['divider', 'Divider'], ['spacer', 'Spacer']];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function EmailStudio({ campaigns = [] }) {
  const [emails, setEmails] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState('');
  const [brief, setBrief] = useState('');
  const [testTo, setTestTo] = useState('');
  const [stats, setStats] = useState(null);
  const [integrations, setIntegrations] = useState(null);

  const load = useCallback(() => {
    suiteAPI.listEmails().then(r => setEmails(r.data)).finally(() => setLoading(false));
    suiteAPI.getLists().then(r => setLists(r.data));
    suiteAPI.getIntegrations().then(r => setIntegrations(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const aiGenerate = async () => {
    if (!brief.trim()) return toast.error('What is this email about?');
    setBusy('ai');
    try {
      const { data } = await suiteAPI.aiEmail({ brief });
      setEditing(data); setBrief(''); load();
      toast.success('Email drafted — edit anything');
    } catch { toast.error('Generation failed'); }
    finally { setBusy(''); }
  };

  const newBlank = () => setEditing({ name: 'Untitled Email', subject: '', preview_text: '', blocks: [], list_ids: [], theme: {} });

  const save = async () => {
    setBusy('save');
    try {
      const { data } = await suiteAPI.saveEmail(editing);
      setEditing(data); load(); toast.success('Saved');
    } catch { toast.error('Save failed'); }
    finally { setBusy(''); }
  };

  const sendTest = async () => {
    if (!editing?.id) return toast.error('Save the email first');
    if (!testTo) return toast.error('Enter a test address');
    setBusy('test');
    try { await suiteAPI.testEmail(editing.id, { to: testTo }); toast.success(`Test sent to ${testTo}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); }
    finally { setBusy(''); }
  };

  const sendAll = async () => {
    if (!editing?.id) return toast.error('Save the email first');
    if (!editing.list_ids?.length) return toast.error('Pick at least one list');
    setBusy('send');
    try {
      const { data } = await suiteAPI.sendEmail(editing.id, { list_ids: editing.list_ids });
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(''); }
  };

  const addBlock = (type) => setEditing(e => ({
    ...e, blocks: [...(e.blocks || []), { id: uid(), type, text: type === 'heading' ? 'Big headline' : type === 'button' ? 'Shop now' : 'Write something great here.', href: '#' }],
  }));
  const patchBlock = (id, changes) => setEditing(e => ({ ...e, blocks: e.blocks.map(b => b.id === id ? { ...b, ...changes } : b) }));
  const moveBlock = (idx, dir) => setEditing(e => {
    const arr = [...e.blocks]; const to = idx + dir;
    if (to < 0 || to >= arr.length) return e;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    return { ...e, blocks: arr };
  });

  if (editing) {
    return (
      <Panel title="Email Builder" subtitle={integrations && !integrations.email.configured ? 'Sending is not configured yet — you can build and preview now, then add a Resend API key in Integrations to deliver.' : `Sending from ${integrations?.email.from_email}`} testId="email-builder"
        action={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setEditing(null)} className="bg-zinc-800 text-white">Back</Button>
            <Button onClick={save} disabled={busy === 'save'} className="bg-zinc-800 text-white" data-testid="email-save">
              {busy === 'save' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save
            </Button>
            <Button onClick={sendAll} disabled={busy === 'send'} className="bg-[#b8ff00] text-black font-bold" data-testid="email-send">
              {busy === 'send' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />} Send campaign
            </Button>
          </div>
        }>
        <div className="grid xl:grid-cols-[380px_1fr] gap-5">
          <div className="space-y-3">
            <Card className="p-5 space-y-3">
              <Field label="Internal name"><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="email-name" /></Field>
              <Field label="Subject line"><Input value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })} className={inputCls} data-testid="email-subject" /></Field>
              <Field label="Preview text"><Input value={editing.preview_text} onChange={e => setEditing({ ...editing, preview_text: e.target.value })} className={inputCls} /></Field>
              {(editing.alt_subjects || []).length > 0 && (
                <div>
                  <p className="text-[11px] text-zinc-500 mb-1">AI subject alternatives (click to use)</p>
                  {editing.alt_subjects.map((s, i) => (
                    <button key={i} onClick={() => setEditing({ ...editing, subject: s })} className="block text-left text-[11px] text-zinc-400 hover:text-[#b8ff00] py-0.5">{s}</button>
                  ))}
                </div>
              )}
              <Field label="Audience lists">
                <div className="space-y-1.5">
                  {lists.length === 0 && <p className="text-[11px] text-zinc-600">No lists yet — create one in Audience.</p>}
                  {lists.map(l => (
                    <label key={l.id} className="flex items-center gap-2 text-xs text-zinc-300">
                      <input type="checkbox" className="accent-[#b8ff00]" data-testid={`email-list-${l.id}`}
                        checked={(editing.list_ids || []).includes(l.id)}
                        onChange={e => setEditing(ed => ({ ...ed, list_ids: e.target.checked ? [...(ed.list_ids || []), l.id] : ed.list_ids.filter(x => x !== l.id) }))} />
                      {l.name} <span className="text-zinc-600">({l.contact_count})</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Campaign">
                <select value={editing.campaign_id || ''} onChange={e => setEditing({ ...editing, campaign_id: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2">
                  <option value="">None</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <div className="flex gap-2">
                <Input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@company.com" className={inputCls} data-testid="email-test-to" />
                <Button onClick={sendTest} disabled={busy === 'test'} className="bg-zinc-800 text-white shrink-0" data-testid="email-test-btn"><TestTube2 size={14} /></Button>
              </div>
            </Card>

            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Add block</p>
              <div className="flex flex-wrap gap-1.5">
                {BLOCK_TYPES.map(([v, l]) => <Chip key={v} onClick={() => addBlock(v)} testId={`email-add-${v}`}>{l}</Chip>)}
              </div>
            </Card>
          </div>

          <div className="space-y-3">
            {(editing.blocks || []).map((b, i) => (
              <Card key={b.id} className="p-4" data-testid={`email-block-${b.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500">{b.type}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-zinc-500" onClick={() => moveBlock(i, -1)}><ArrowUp size={12} /></Button>
                    <Button size="sm" variant="ghost" className="text-zinc-500" onClick={() => moveBlock(i, 1)}><ArrowDown size={12} /></Button>
                    <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => setEditing(e => ({ ...e, blocks: e.blocks.filter(x => x.id !== b.id) }))}><Trash2 size={12} /></Button>
                  </div>
                </div>
                {['heading', 'text', 'button'].includes(b.type) && (
                  <Textarea value={b.text || ''} onChange={e => patchBlock(b.id, { text: e.target.value })} rows={b.type === 'text' ? 3 : 1} className={inputCls} />
                )}
                {b.type === 'button' && <Input value={b.href || ''} onChange={e => patchBlock(b.id, { href: e.target.value })} placeholder="https://destination" className={`${inputCls} mt-2`} />}
                {b.type === 'image' && <Input value={b.src || ''} onChange={e => patchBlock(b.id, { src: e.target.value })} placeholder="Image URL (use an asset library link)" className={inputCls} />}
              </Card>
            ))}
            {(editing.blocks || []).length === 0 && <EmptyState icon={Mail} text="Add blocks to build your email, or let AI draft one for you." />}
            {editing.id && (
              <div className="bg-white rounded-xl overflow-hidden" data-testid="email-preview">
                <div className="bg-zinc-100 px-4 py-2 text-[11px] text-zinc-600 flex items-center gap-1.5"><Eye size={11} /> Live preview</div>
                <EmailPreview id={editing.id} />
              </div>
            )}
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Email Campaigns" subtitle="Draft with AI, build with blocks, test, send and track opens and clicks." testId="suite-email"
      action={<Button onClick={newBlank} className="bg-[#b8ff00] text-black font-bold" data-testid="email-new"><Plus size={15} className="mr-1.5" /> New email</Button>}>
      <Card className="p-5 mb-5 space-y-3">
        <Field label="Let AI write it" hint="It returns subject lines, body copy and a CTA you can edit.">
          <Textarea value={brief} onChange={e => setBrief(e.target.value)} rows={2} placeholder="Announce our September jacket drop, 200 units, early access for subscribers" className={inputCls} data-testid="email-ai-brief" />
        </Field>
        <Button onClick={aiGenerate} disabled={busy === 'ai'} className="bg-violet-500 text-white" data-testid="email-ai-generate">
          {busy === 'ai' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Draft email with AI
        </Button>
      </Card>

      {loading ? <PanelLoader /> : emails.length === 0 ? <EmptyState icon={Mail} text="No emails yet." /> : (
        <div className="space-y-2">
          {emails.map(e => (
            <Card key={e.id} className="p-4 flex items-center justify-between gap-3" data-testid={`email-item-${e.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{e.name}</p>
                <p className="text-[11px] text-zinc-500 truncate">{e.subject || 'No subject yet'}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${e.status === 'sent' ? 'bg-[#b8ff00]/15 text-[#b8ff00]' : 'bg-zinc-800 text-zinc-400'}`}>{e.status?.replace(/_/g, ' ')}</span>
                <span className="text-[11px] text-zinc-500">{e.sent_count} sent · {e.opens} opens · {e.clicks} clicks</span>
                <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => suiteAPI.emailStats(e.id).then(r => setStats({ ...r.data, name: e.name }))} data-testid={`email-stats-${e.id}`}><BarChart3 size={13} /></Button>
                <Button size="sm" className="bg-zinc-800 text-white text-xs" onClick={() => suiteAPI.getEmail(e.id).then(r => setEditing(r.data))} data-testid={`email-edit-${e.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteEmail(e.id).then(load)}><Trash2 size={13} /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {stats && (
        <Card className="p-5 mt-4" data-testid="email-stats-panel">
          <div className="flex justify-between mb-3">
            <h3 className="text-sm font-bold text-white">{stats.name} performance</h3>
            <Button size="sm" variant="ghost" className="text-zinc-500" onClick={() => setStats(null)}>Close</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[['Recipients', stats.recipients], ['Sent', stats.sent], ['Opened', `${stats.opened} (${stats.open_rate}%)`], ['Clicked', `${stats.clicked} (${stats.click_rate}%)`], ['Failed', stats.failed]].map(([l, v]) => (
              <div key={l}><p className="text-[11px] uppercase tracking-wider text-zinc-500">{l}</p><p className="text-lg font-bold text-white">{v}</p></div>
            ))}
          </div>
        </Card>
      )}
    </Panel>
  );
}

function EmailPreview({ id }) {
  const [html, setHtml] = useState('');
  useEffect(() => { suiteAPI.getEmail(id).then(r => setHtml((r.data.html || '').replace('{{UNSUB}}', '#'))); }, [id]);
  return <iframe title="email-preview" srcDoc={html} className="w-full h-[520px] bg-white" />;
}
